import { zodResolver } from '@hookform/resolvers/zod';
import { DateOnly, JobCreate, JobStatus, type JobResponse } from '@siteday/shared';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { qk, quickbooksApi, usersApi } from '@/api';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';

/** Form shape: strings for the inputs, split back to the shared JobCreate on submit. */
export const JobFormSchema = JobCreate.omit({
  clientEmails: true,
  foremanIds: true,
  startDate: true,
}).extend({
  /** A cleared <input type="date"> yields ''; the shared DateOnly rule applies once it is non-empty. */
  startDate: z
    .union([DateOnly, z.literal('')])
    .nullable()
    .optional(),
  clientEmails: z.string().default(''),
  foremanIds: z.array(z.string().uuid()).default([]),
});
export type JobFormValues = z.input<typeof JobFormSchema>;

export function toJobCreate(v: z.output<typeof JobFormSchema>): z.input<typeof JobCreate> {
  const emails = v.clientEmails
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    name: v.name,
    clientName: v.clientName?.trim() ? v.clientName.trim() : null,
    siteAddress: v.siteAddress?.trim() ? v.siteAddress.trim() : null,
    status: v.status,
    startDate: v.startDate ? v.startDate : null,
    qboCustomerId: v.qboCustomerId?.trim() ? v.qboCustomerId.trim() : null,
    foremanIds: v.foremanIds,
    clientEmails: emails,
  };
}

export function useForemen() {
  return useQuery({
    queryKey: qk.users.list({ role: 'foreman' }),
    queryFn: () => usersApi.listUsers({ role: 'foreman' }),
  });
}

export function useQboStatus() {
  return useQuery({
    queryKey: qk.quickbooks.status(),
    queryFn: quickbooksApi.getStatus,
    staleTime: 60_000,
  });
}

export function useQboCandidates(enabled: boolean) {
  return useQuery({
    queryKey: qk.quickbooks.candidates(),
    queryFn: quickbooksApi.getCandidates,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export interface JobFormProps {
  job?: JobResponse | undefined;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: z.output<typeof JobFormSchema>) => void;
  onCancel?: (() => void) | undefined;
  formId?: string | undefined;
  /** Hide foremen when a separate control manages them (job settings tab). */
  withForemen?: boolean | undefined;
}

/** W5 new job / W3 settings: name, client, site address, start date, foremen, QuickBooks customer, status. */
export function JobForm({
  job,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
  formId = 'job-form',
  withForemen = true,
}: JobFormProps) {
  const foremen = useForemen();
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);

  const form = useForm<JobFormValues, unknown, z.output<typeof JobFormSchema>>({
    resolver: zodResolver(JobFormSchema),
    defaultValues: {
      name: job?.name ?? '',
      clientName: job?.clientName ?? '',
      siteAddress: job?.siteAddress ?? '',
      status: job?.status ?? 'active',
      startDate: job?.startDate ?? '',
      qboCustomerId: job?.qboCustomerId ?? '',
      foremanIds: job?.foremen.map((f) => f.id) ?? [],
      clientEmails: job?.clientEmails.join(', ') ?? '',
    },
  });
  const errors = form.formState.errors;
  const selectedForemen = form.watch('foremanIds') ?? [];

  return (
    <form id={formId} noValidate onSubmit={form.handleSubmit(onSubmit)} className="form-grid">
      <Field label="Job name" htmlFor="job-name" error={errors.name?.message} wide>
        <Input
          id="job-name"
          autoFocus
          invalid={!!errors.name}
          placeholder="Oak Ridge Phase 2 — Storm sewer"
          {...form.register('name')}
        />
      </Field>
      <Field label="Client" htmlFor="job-client" error={errors.clientName?.message}>
        <Input id="job-client" {...form.register('clientName')} />
      </Field>
      <Field label="Status" htmlFor="job-status">
        <Select id="job-status" {...form.register('status')}>
          {JobStatus.options.map((s) => (
            <option key={s} value={s}>
              {s[0]!.toUpperCase() + s.slice(1)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Site address" htmlFor="job-address" error={errors.siteAddress?.message} wide>
        <Input id="job-address" {...form.register('siteAddress')} />
      </Field>
      <Field label="Start date" htmlFor="job-start" error={errors.startDate?.message}>
        <Input id="job-start" type="date" {...form.register('startDate')} />
      </Field>
      <Field
        label="QuickBooks customer"
        htmlFor="job-qbo"
        hint={
          qbo.data?.connected ? 'From the last import' : 'Connect QuickBooks to pick from customers'
        }
      >
        {qbo.data?.connected && candidates.data ? (
          <Select id="job-qbo" {...form.register('qboCustomerId')}>
            <option value="">Not mapped</option>
            {candidates.data.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id="job-qbo"
            placeholder="Customer id (optional)"
            {...form.register('qboCustomerId')}
          />
        )}
      </Field>
      <Field
        label="Client report emails"
        htmlFor="job-emails"
        hint="Comma separated. Overrides the company default list."
        error={errors.clientEmails?.message}
        wide
      >
        <Input
          id="job-emails"
          placeholder="pm@client.example, super@client.example"
          {...form.register('clientEmails')}
        />
      </Field>
      {withForemen ? (
        <Field label="Foremen" wide hint="Foremen see this job in their phone app.">
          <div className="flex flex-wrap gap-2">
            {foremen.data?.items.map((f) => {
              const on = selectedForemen.includes(f.id);
              return (
                <label
                  key={f.id}
                  className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-medium ${on ? 'border-ink bg-ink text-on-ink' : 'border-line bg-surface'}`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={(e) =>
                      form.setValue(
                        'foremanIds',
                        e.target.checked
                          ? [...selectedForemen, f.id]
                          : selectedForemen.filter((x) => x !== f.id),
                        { shouldDirty: true },
                      )
                    }
                  />
                  {f.name}
                </label>
              );
            })}
            {foremen.data && foremen.data.items.length === 0 ? (
              <span className="text-ink3">No foremen yet — invite one under People.</span>
            ) : null}
          </div>
        </Field>
      ) : null}
      <div className="w mt-2 flex justify-end gap-2">
        {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
        <Button type="submit" variant="primary" loading={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
